"use client";

import React, { FC, PropsWithChildren } from "react";
import {
  GlobalStateProvider,
  ShaApplicationProvider,
  StoredFilesProvider,
  useNextRouter,
} from "@shesha-io/reactjs";

import { AppProgressBar } from "next-nprogress-bar";
import { WorkflowApplicationPlugin } from "@shesha-io/workflow";
import { CURRENT_APPOINTMENT_HEADER, EnterpriseApplicationPlugin } from "@shesha-io/enterprise";
import { useTheme } from "antd-style";
import { replaceAppIdInPath, urlContainsAppId } from "@/routing";
import { DocumentViewerActionAccessor } from "@/components/nutrient-pspdf/configurable-actions/document-viewer-processor";
import { ESignSigningActionAccessor } from "@/components/esign-signing/configurable-actions/esign-signing-processor";
import { ApprovalsApplicationPlugin } from "@shesha-io/pd-approvals";
import { ContentApplicationPlugin } from "@shesha-io/content";
import { BudgetApplicationPlugin } from "@shesha-io/budget";

export interface IAppProviderProps {
  backendUrl: string;
  positionAppointmentId?: string;
}

export const AppProvider: FC<PropsWithChildren<IAppProviderProps>> = ({
  children,
  backendUrl,
  positionAppointmentId,
}) => {
  const nextRouter = useNextRouter();
  const theme = useTheme();

  const buildUrlWithAppId = (url: string): string => {
    return positionAppointmentId && !urlContainsAppId(url)
      ? replaceAppIdInPath(url, positionAppointmentId)
      : url;
  };

  return (
    <GlobalStateProvider>
      <AppProgressBar height="4px" color={theme.colorPrimary} shallowRouting />
      <ShaApplicationProvider
        backendUrl={backendUrl}
        router={nextRouter}
        noAuth={nextRouter.path?.includes('/no-auth')}
        buildHttpRequestHeaders={() => {
          return { [CURRENT_APPOINTMENT_HEADER]: positionAppointmentId };
        }}
        urlOverrideFunc={buildUrlWithAppId}
      >
        <EnterpriseApplicationPlugin>
          <ApprovalsApplicationPlugin>
            <WorkflowApplicationPlugin>
              <StoredFilesProvider baseUrl={backendUrl} ownerId={""} ownerType={""}>
                <ContentApplicationPlugin>
                  <BudgetApplicationPlugin>
                    <DocumentViewerActionAccessor>
                      <ESignSigningActionAccessor>
                        {children}
                      </ESignSigningActionAccessor>
                    </DocumentViewerActionAccessor>
                  </BudgetApplicationPlugin>
                </ContentApplicationPlugin>
              </StoredFilesProvider>
            </WorkflowApplicationPlugin>
          </ApprovalsApplicationPlugin>
        </EnterpriseApplicationPlugin>
      </ShaApplicationProvider>
    </GlobalStateProvider>
  );
};
